import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

interface ModalProps {
  open: boolean
  onClose: () => void
  children: ReactNode
  /** id of the title element for aria-labelledby */
  labelledBy?: string
  panelClassName?: string
}

/** Full-viewport overlay; sits above mobile bottom nav (z-50). */
export function Modal({ open, onClose, children, labelledBy, panelClassName }: ModalProps) {
  if (!open) return null

  return (
    <div className="fixed inset-0 z-[60]">
      <button
        type="button"
        className="fixed inset-0 bg-navy/40"
        aria-label="Close dialog"
        onClick={onClose}
      />
      <div className="fixed inset-0 overflow-y-auto overscroll-contain">
        <div className="flex min-h-full items-end justify-center p-4 pb-[calc(5rem+env(safe-area-inset-bottom,0px))] sm:items-center sm:p-4 sm:pb-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={labelledBy}
            className={cn(
              'relative flex w-full max-w-lg flex-col overflow-hidden rounded border border-border-2 bg-card shadow-[0_8px_32px_rgba(11,37,69,0.12)]',
              'max-h-[min(90dvh,calc(100dvh-5rem))] sm:max-h-[90vh]',
              panelClassName,
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}

export function ModalHeader({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('shrink-0 border-b border-border-2 px-6 py-4', className)}>{children}</div>
  )
}

export function ModalBody({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('min-h-0 flex-1 overflow-y-auto px-6 py-4', className)}>{children}</div>
}

export function ModalFooter({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        // flex-wrap is the load-bearing part here: a modal with several conditional action
        // buttons (e.g. invoice detail's Void + Record payment + Mark as sent, all independently
        // gated) can exceed one row's width — without wrapping, buttons silently overflow off the
        // edge instead of dropping to a second line.
        'flex flex-wrap items-center justify-end gap-3 shrink-0 border-t border-border-2 bg-card px-6 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]',
        className,
      )}
    >
      {children}
    </div>
  )
}
